import Rss16 from '@carbon/icons-react/es/rss/16';
import { Tooltip } from 'antd';
import isEqual from 'lodash/isEqual';
import React, { FC, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { ProjectId } from 'api/models';
import { Button } from 'components/atoms/button';
import { Picker } from './picker';
import { SquadPopover } from './popover';
import { SquadOptions } from './squad-options';
import { Option, SquadPayload, Value } from './types';
import { useCustomInviteUserToProjectConfirmation } from './use-invite-user';
import {
  filterUserOptions,
  nonProjectUsers,
  optionsToSquadPayload,
} from './utils';

interface FollowersPickerProps {
  value: Option[];
  projectId: ProjectId;
  onChange: (value: SquadPayload) => void;
  isPrivate?: boolean;
}

export const TaskFollowersPicker: FC<FollowersPickerProps> = (props) => {
  const { onChange, value, projectId, isPrivate } = props;
  const [selected, setSelected] = useState<Value>(value);

  useDeepCompareEffect(() => {
    setSelected(value);
  }, [value]);

  const confirm = useCustomInviteUserToProjectConfirmation(projectId);

  const commit = (_selected) => {
    onChange(optionsToSquadPayload((_selected as Option[]) || []));
  };

  const onCancel = (_selected: Option[]) => {
    const result = _selected.filter(
      (o) => o.extra.type === 'team' || o.invited,
    );

    setSelected(result as Value);
    if (!isEqual(value, result)) {
      commit(result);
    }
  };

  const handleClose = () => {
    if (!isEqual(value, selected)) {
      const nonProjectUsersList = filterUserOptions(
        selected as Option[],
      ).filter(nonProjectUsers);

      if (nonProjectUsersList.length) {
        confirm(
          nonProjectUsersList,
          selected as Option[],
          value,
          commit,
          onCancel,
        );
      } else {
        commit(selected);
      }
    }
  };

  return (
    <SquadPopover
      onClose={handleClose}
      content={
        <SquadOptions
          projectId={projectId}
          selected={value}
          isPrivate={isPrivate}
        >
          {({ data: options, isLoading, isFetching }) => (
            <Picker
              options={options}
              value={selected}
              isLoading={isLoading || isFetching}
              onChange={setSelected}
            />
          )}
        </SquadOptions>
      }
    >
      <Tooltip title={<FormattedMessage id="task.followers" />}>
        <Button
          type={selected && selected.length > 0 ? 'link' : 'text'}
          size="small"
          icon={<Rss16 />}
          data-cy="followers.picker.trigger"
        />
      </Tooltip>
    </SquadPopover>
  );
};
